import { useState } from 'react'
import { NavLink } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, apiTokensApi, get, hostError, type APITokenCreated } from '../api'
import { useSession } from '../session'
import { Button, Notice, Panel } from '../components/ui'
import { t } from '../i18n'
import { CreateForm, ShownOnce, TokenList, type CreateInput } from '../pages/ApiTokensParts'
import { FactorBadge, FactorCancelled, SecondFactorProvider, useSecondFactor } from './SecondFactor'

/*
 * Console → Settings → API tokens (docs/api-tokens.md §8.2): admin tokens
 * (sxak_, admin scopes, 7–90 days) of every admin, created with the admin's
 * second factor as the network actions (a 403 totp_required opens the code
 * window; a center without a provisioner, second_factor inline, asks for no
 * code), and every user's API tokens with Revoke (support, incidents).
 */

const ADMIN_KEY = ['admin-api-tokens'] as const
const USER_KEY = ['admin-user-tokens'] as const
const SF_KEY = ['admin-api-tokens', 'second-factor'] as const

/** Settings' sections: Output bitrate (the page itself) and API tokens. */
export function SettingsTabs() {
  const tab = ({ isActive }: { isActive: boolean }) =>
    `-mb-px inline-flex min-h-9 items-center border-b-2 px-3 text-sm max-md:min-h-[44px] ${isActive ? 'border-accent font-medium text-text' : 'border-transparent text-muted hover:text-text-soft'}`
  return (
    <nav aria-label="Settings sections" className="flex gap-1 border-b border-line" data-testid="settings-tabs">
      <NavLink to="/admin/settings" end className={tab}>
        Output bitrate
      </NavLink>
      <NavLink to="/admin/settings/api-tokens" className={tab} data-testid="settings-tab-api-tokens">
        API tokens
      </NavLink>
    </nav>
  )
}

/** A GET the token's scopes open, for the "Try it" line. */
const EXAMPLE: [string, string][] = [
  ['admin:nodes:read', '/api/admin/nodes'],
  ['admin:routes:read', '/api/admin/relay/routes'],
  ['admin:streams:read', '/api/admin/streams'],
  ['admin:audit:read', '/api/admin/audit'],
]
export const examplePath = (scopes: string[]) => EXAMPLE.find(([s]) => scopes.includes(s))?.[1] ?? '/api/admin/nodes'

/** The console's name: admin tokens work there only. */
function consoleBase(adminURL?: string, area?: string): string {
  if (area === 'app' && adminURL) return adminURL.replace(/\/+$/, '')
  return location.origin
}

/** provisioner (a code is asked) or inline (none); a center without the relay API asks as the provisioner says. */
async function secondFactorMode(): Promise<'provisioner' | 'inline'> {
  try {
    const s = await get<{ second_factor?: 'provisioner' | 'inline' }>('/api/admin/relay/settings')
    return s.second_factor === 'inline' ? 'inline' : 'provisioner'
  } catch {
    return 'provisioner'
  }
}

export function AdminApiTokens() {
  return (
    <SecondFactorProvider>
      <Page />
    </SecondFactorProvider>
  )
}

/** The text of a refused create that is not a step of the code window. */
function createError(e: unknown): Error {
  const h = hostError(e)
  if (h?.code === 'provisioner_unavailable') return new Error('the center could not check your code (the provisioner did not answer). Nothing changed; try again in a minute.')
  if (h?.code === 'totp_not_enrolled') return new Error('your account has no second factor yet. Nothing changed.')
  if (h?.code === 'totp_locked') return new Error(`too many wrong codes, confirmations are locked${h.retry_after_s ? ` for ${Math.ceil(h.retry_after_s / 60)} min` : ''}.`)
  return e instanceof Error ? e : new Error(String(e))
}

function Page() {
  const qc = useQueryClient()
  const { user } = useSession()
  const factor = useSecondFactor()
  const admins = useQuery({ queryKey: ADMIN_KEY, queryFn: apiTokensApi.adminList })
  const users = useQuery({ queryKey: USER_KEY, queryFn: apiTokensApi.userTokens })
  const sf = useQuery({ queryKey: SF_KEY, queryFn: secondFactorMode, staleTime: 60_000 })
  const inline = sf.data === 'inline'
  const [form, setForm] = useState(false)
  // The secret: here only, while the panel is open.
  const [created, setCreated] = useState<APITokenCreated | null>(null)

  const create = async (b: CreateInput) => {
    let r: APITokenCreated
    try {
      r = await factor((totp) => apiTokensApi.adminCreate({ ...b, ...(totp ? { totp } : {}) }))
    } catch (e) {
      throw e instanceof FactorCancelled ? e : createError(e)
    }
    setForm(false)
    setCreated(r)
    await qc.invalidateQueries({ queryKey: ADMIN_KEY })
  }
  const revokeAdmin = async (id: number) => {
    await apiTokensApi.adminRevoke(id)
    await qc.invalidateQueries({ queryKey: ADMIN_KEY })
  }
  const revokeUser = async (id: number) => {
    await apiTokensApi.revokeUserToken(id)
    await qc.invalidateQueries({ queryKey: USER_KEY })
  }

  const data = admins.data
  const mine = data?.tokens.filter((x) => x.user_id === undefined || x.user_id === user?.id).length ?? 0
  const full = !!data && mine >= data.limit
  return (
    <div className="space-y-6" data-testid="admin-api-tokens">
      <div>
        <h1 className="text-xl font-medium">Settings</h1>
        <p className="text-sm text-muted">API tokens: scripts and dashboards that call the API without a browser.</p>
      </div>
      <SettingsTabs />
      {created && (
        <ShownOnce console created={created} base={consoleBase(user?.admin_url, user?.area)} path={examplePath(created.api_token.scopes)} onClose={() => setCreated(null)} />
      )}
      <Panel title="Admin tokens" aside={!inline && sf.data ? <FactorBadge /> : undefined}>
        <div className="flex flex-col gap-4" data-testid="admin-tokens" data-second-factor={sf.data}>
          <p className="m-0 max-w-3xl text-sm text-text-soft">
            Tokens <code className="font-mono text-xs">sxak_…</code> work on the admin API of this console name only, for 7 to 90 days, with the permissions you pick. They never pass
            an action that needs your code (revoke, drain, turning the relay off with live legs): those stay in the console.
          </p>
          {admins.isPending ? (
            <p className="text-sm text-muted">Loading…</p>
          ) : admins.isError || !data ? (
            <div className="flex flex-wrap items-center gap-3">
              <Notice>Couldn’t load admin tokens: {(admins.error as Error)?.message}</Notice>
              <Button onClick={() => admins.refetch()}>Try again</Button>
            </div>
          ) : (
            <>
              {form ? (
                <CreateForm
                  console
                  list={data}
                  onCreate={create}
                  onCancel={() => setForm(false)}
                  quiet={(e) => e instanceof FactorCancelled}
                  extra={
                    <p className="m-0 text-xs text-muted" data-testid="admin-token-code-note">
                      {inline ? 'This center has no second-factor service: the token is created without a code.' : 'Creating it asks for the 6-digit code from your authenticator app (one code covers a minute).'}
                    </p>
                  }
                />
              ) : (
                !created && (
                  <div className="flex flex-wrap items-center gap-3">
                    <Button variant="primary" disabled={full} onClick={() => setForm(true)} data-testid="admin-tokens-create" className="max-md:min-h-[44px]">
                      Create admin token
                    </Button>
                    <span className={`text-sm ${full ? 'text-warn' : 'text-muted'}`} data-testid="admin-tokens-count">
                      Yours: {full ? t('apiTokens.limit', { n: mine, max: data.limit }) : t('apiTokens.count', { n: mine, max: data.limit })}
                    </span>
                  </div>
                )
              )}
              {data.tokens.length === 0 ? (
                <p className="m-0 text-sm text-muted" data-testid="admin-tokens-empty">
                  No admin tokens.
                </p>
              ) : (
                <TokenList tokens={data.tokens} onRevoke={revokeAdmin} owner me={user?.id} testId="admin-token-list" />
              )}
            </>
          )}
        </div>
      </Panel>
      <Panel title="User tokens">
        <div className="flex flex-col gap-4" data-testid="user-tokens">
          <p className="m-0 max-w-3xl text-sm text-text-soft">Every user’s active API tokens. Revoke one when it leaked or misbehaves: it stops at once, and its owner sees it gone from Account → API tokens.</p>
          {users.isPending ? (
            <p className="text-sm text-muted">Loading…</p>
          ) : users.isError ? (
            <div className="flex flex-wrap items-center gap-3">
              <Notice>
                Couldn’t load user tokens: {users.error instanceof ApiError ? users.error.message : (users.error as Error).message}
              </Notice>
              <Button onClick={() => users.refetch()}>Try again</Button>
            </div>
          ) : users.data.tokens.length === 0 ? (
            <p className="m-0 text-sm text-muted" data-testid="user-tokens-empty">
              No user has an API token.
            </p>
          ) : (
            <TokenList tokens={users.data.tokens} onRevoke={revokeUser} owner testId="user-token-list" />
          )}
        </div>
      </Panel>
    </div>
  )
}
